import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { EnvelopeSimple, MapPin } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { sendEmailJS } from '../utils/emailjs';

const ContactPage = () => {
  const { t } = useTranslation();
  const form = useRef();
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState(null);

  const sendEmail = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setError(null);
    setIsSent(false);

    try {
      await sendEmailJS(form.current, process.env.REACT_APP_EMAILJS_TEMPLATE_ID, {
        onSuccess: () => {
          setIsSent(true);
          form.current.reset();
        },
        onError: (error) => {
          setError(t('contactPage.form.errorMessage'));
        }
      });
    } catch (error) {
      // Error is already handled in onError callback
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="bg-white py-16 sm:py-24">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold font-heading mb-4">{t('contactPage.title')}</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('contactPage.subtitle')}
            </p>
          </motion.div>
        </div>

        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-start">
          
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-3xl font-bold font-heading mb-6">{t('contactPage.getInTouch')}</h2>
            <p className="text-gray-600 mb-8">
              <Trans i18nKey="contactPage.reachOut">
                Reach out to us through any of the channels below. For project inquiries, please{' '}
                <Link to="/start-your-project" className="text-[#0D2B45] font-medium hover:underline">
                  use our dedicated project form.
                </Link>
              </Trans>
            </p>
            <div className="space-y-6">
              <a href="mailto:info@juliux.nl" className="flex items-center gap-4 text-gray-700 hover:text-black transition-colors">
                <EnvelopeSimple size={24} className="text-[#0D2B45]" />
                <span className="font-medium">info@juliux.nl</span>
              </a>
              <div className="flex items-center gap-4 text-gray-700">
                <MapPin size={24} className="text-[#0D2B45]" />
                <span className="font-medium">{t('contactPage.location')}</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-gray-50 p-8 sm:p-10 rounded-lg shadow-sm"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <form ref={form} onSubmit={sendEmail} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">{t('contactPage.form.fullName')}</label>
                <input type="text" id="name" name="name" required className="mt-1 block w-full px-4 py-3 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0D2B45] focus:border-[#0D2B45]" />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">{t('contactPage.form.emailAddress')}</label>
                <input type="email" id="email" name="from_email" required className="mt-1 block w-full px-4 py-3 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0D2B45] focus:border-[#0D2B45]" />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">{t('contactPage.form.message')}</label>
                <textarea id="message" name="message" rows="5" required className="mt-1 block w-full px-4 py-3 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0D2B45] focus:border-[#0D2B45]"></textarea>
              </div>
              <div>
                <button type="submit" disabled={isSending || isSent} className="w-full inline-flex justify-center items-center gap-2 bg-[#0D2B45] text-white px-6 py-3 rounded-lg hover:bg-opacity-90 transition-colors duration-300 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed">
                  {isSending ? t('contactPage.form.sendingButton') : isSent ? t('contactPage.form.sentButton') : t('contactPage.form.sendButton')}
                </button>
              </div>
              {error && <p className="text-red-500 text-sm mt-2 text-center">{error}</p>}
              {isSent && <p className="text-green-600 text-sm mt-2 text-center bg-green-100 p-3 rounded-md">{t('contactPage.form.successMessage')}</p>}
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage; 